import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient, BACKEND_URL, useAuth } from '../context/AuthContext';
import PdfPreviewModal from './PdfPreviewModal';
import logger from '../lib/logger';
import { getErrorMessage } from '../lib/errorHandler';

const GfMerger = ({ configuracion, cuentas = [] }) => {
    const { showNotification, confirm } = useAuth();
    const [files, setFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [gfGenerating, setGfGenerating] = useState(false);
    const [savedGfs, setSavedGfs] = useState([]);
    const [selectedCuentaId, setSelectedCuentaId] = useState('');
    const [filterMonth, setFilterMonth] = useState('ALL');
    const [previewConfig, setPreviewConfig] = useState(null);

    const cargarGfs = useCallback(async () => {
        try {
            const response = await apiClient.get('/gf');
            setSavedGfs(response.data);
        } catch (error) {
            logger.error('Error cargando GFs:', error);
        }
    }, []);

    // Sincronizar filtro con cuenta seleccionada
    useEffect(() => {
        if (selectedCuentaId) {
            const cuenta = cuentas.find(c => c.id === selectedCuentaId);
            if (cuenta && cuenta.mes_cobro) {
                const mes = cuenta.mes_cobro.split(' de ')[0].toUpperCase();
                setFilterMonth(mes);
            }
        } else {
            setFilterMonth('ALL');
        }
    }, [selectedCuentaId, cuentas]);

    useEffect(() => {
        cargarGfs();
        if (cuentas.length > 0 && !selectedCuentaId) {
            setSelectedCuentaId(cuentas[0].id);
        }
    }, [cargarGfs, cuentas, selectedCuentaId]);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files).filter(f => f.type === 'application/pdf');
        setFiles(prev => [...prev, ...selectedFiles]);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFiles = Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf');
        setFiles(prev => [...prev, ...droppedFiles]);
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Abrir modal de vista previa para un GF dado (url absoluta + metadata)
    const abrirPreview = (pdfUrl, metadata) => {
        const fileName = `GF_${metadata.cedula || 'ID'}_${metadata.rp || 'RP'}_${metadata.mes || 'MES'}.pdf`.replace(/\s+/g, '_');
        setPreviewConfig({
            title: `Gestión Financiera — ${metadata.mes || 'Documento'}`,
            subtitle: fileName,
            tabs: [
                {
                    key: 'gf',
                    label: '📄 GF Fusionado',
                    loadBlobUrl: async () => {
                        const path = pdfUrl.replace(BACKEND_URL, '').replace(/^\/api/, '');
                        const resp = await apiClient.get(path, { responseType: 'blob' });
                        const blob = new Blob([resp.data], { type: 'application/pdf' });
                        const url = URL.createObjectURL(blob);
                        return { url, filename: fileName };
                    },
                },
            ],
        });
    };

    const handleGenerarGF = async () => {
        if (files.length === 0) {
            showNotification('Por favor añade al menos un archivo PDF.', 'error');
            return;
        }

        setGfGenerating(true);
        try {
            const formData = new FormData();
            files.forEach(file => formData.append('files', file));
            if (selectedCuentaId) formData.append('cuenta_id', selectedCuentaId);

            const response = await apiClient.post('/gf/merge', formData);
            showNotification('Archivo GF generado exitosamente', 'success');
            const cuenta = cuentas.find(c => c.id === selectedCuentaId);
            const metadata = {
                cedula: configuracion?.cedula,
                rp: configuracion?.numero_rp,
                mes: cuenta?.mes_cobro?.split(' de ')[0]?.toUpperCase() || 'SIN_MES'
            };

            abrirPreview(`${BACKEND_URL}${response.data.pdf_url}`, metadata);
            setFiles([]);
            cargarGfs();
        } catch (error) {
            showNotification(getErrorMessage(error, 'Error al generar GF'), 'error');
        } finally {
            setGfGenerating(false);
        }
    };

    const handleDeleteGf = async (id) => {
        const ok = await confirm({
            title: 'Eliminar registro GF',
            message: '¿Estás seguro de eliminar este registro de GF? Esta acción no se puede deshacer.',
            confirmText: 'Eliminar',
            type: 'danger'
        });

        if (!ok) return;

        try {
            await apiClient.delete(`/gf/${id}`);
            showNotification('GF eliminado correctamente', 'success');
            cargarGfs();
        } catch (error) {
            showNotification(getErrorMessage(error, 'Error al eliminar'), 'error');
        }
    };

    const formatFecha = (isoString) => {
        if (!isoString) return '';
        try {
            const date = new Date(isoString);
            return date.toLocaleString('es-CO', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) { return isoString; }
    };

    const filteredGfs = useMemo(() => {
        if (filterMonth === 'ALL') return savedGfs;
        return savedGfs.filter(gf => gf.mes_nombre === filterMonth);
    }, [savedGfs, filterMonth]);

    const mesesDisponibles = useMemo(() => {
        const m = new Set(savedGfs.map(gf => gf.mes_nombre));
        return Array.from(m).sort();
    }, [savedGfs]);

    return (
        <div className="fade-in max-w-[1400px] mx-auto pb-12 px-4">
            <div className="grid lg:grid-cols-2 gap-8">

                {/* Panel de Creación */}
                <div className="bg-white rounded-[2rem] p-8 shadow-2xl border border-slate-100 relative overflow-hidden">
                    <div className="relative z-10">
                        <div className="flex flex-col gap-4 mb-8">
                            <h2 className="text-2xl font-black text-slate-900 flex items-center gap-3">
                                <span className="bg-indigo-600 text-white p-2.5 rounded-2xl shadow-lg ring-4 ring-indigo-50">🧩</span>
                                Fusión GF
                            </h2>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Vincular con Cuenta</label>
                                <select
                                    value={selectedCuentaId}
                                    onChange={(e) => setSelectedCuentaId(e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                                >
                                    <option value="">Cuenta de Cobro...</option>
                                    {cuentas.map(c => (
                                        <option key={c.id} value={c.id}>{c.mes_cobro}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={onDrop}
                            className={`group relative border-3 border-dashed rounded-[2rem] transition-all p-8 flex flex-col items-center justify-center min-h-[200px] ${isDragging ? 'border-indigo-500 bg-indigo-50 scale-[0.98]' : 'border-slate-200 bg-slate-50 hover:border-indigo-400 hover:bg-white'
                                }`}
                        >
                            <input
                                type="file" multiple accept=".pdf"
                                onChange={handleFileChange}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
                            />
                            <div className={`text-5xl mb-4 transition-transform duration-500 ${isDragging ? 'scale-125' : 'group-hover:scale-110'}`}>📂</div>
                            <p className="text-lg font-black text-slate-800 text-center">
                                {isDragging ? '¡Suéltalos ahora!' : 'Suelta tus PDFs aquí'}
                            </p>
                            <p className="text-slate-400 font-bold text-xs mt-2 bg-white px-4 py-1.5 rounded-full shadow-sm">O selecciona archivos</p>
                        </div>

                        {files.length > 0 && (
                            <div className="mt-6 space-y-4 animate-in slide-in-from-bottom-4">
                                <div className="flex items-center justify-between px-1">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Archivos en cola ({files.length})</span>
                                    <button onClick={() => setFiles([])} className="text-xs font-black text-red-500 hover:text-red-600 transition-colors uppercase tracking-wider">Borrar todo</button>
                                </div>
                                <div className="max-h-48 overflow-y-auto pr-2 space-y-2 custom-scrollbar">
                                    {files.map((file, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 group/item hover:border-indigo-200 hover:bg-white transition-all shadow-sm">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <div className="w-6 h-6 rounded bg-indigo-100 flex items-center justify-center text-indigo-600 font-black text-[10px]">{idx + 1}</div>
                                                <span className="text-xs font-bold text-slate-700 truncate">{file.name}</span>
                                            </div>
                                            <button onClick={() => removeFile(idx)} className="p-1.5 text-slate-300 hover:text-red-500 transition-colors hover:bg-red-50 rounded-lg">
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    onClick={handleGenerarGF}
                                    disabled={gfGenerating}
                                    className="w-full mt-4 h-14 bg-gradient-to-br from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-2xl font-black transition-all shadow-xl shadow-indigo-200 active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-4 group"
                                >
                                    {gfGenerating ? (
                                        <>
                                            <div className="w-5 h-5 border-3 border-white/20 border-t-white rounded-full animate-spin"></div>
                                            GENERANDO...
                                        </>
                                    ) : (
                                        <>
                                            <span>FUSIONAR Y GUARDAR</span>
                                            <span className="text-xl group-hover:translate-x-1 transition-transform">🚀</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Panel de Historial */}
                <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white shadow-2xl relative overflow-hidden group">
                    <div className="flex items-center justify-between mb-8">
                        <h3 className="text-xl font-black flex items-center gap-3">
                            <span className="bg-white/10 p-2 rounded-xl text-indigo-400">📜</span>
                            Historial
                        </h3>
                        <select
                            value={filterMonth}
                            onChange={(e) => setFilterMonth(e.target.value)}
                            className="bg-white/5 border border-white/10 rounded-xl px-3 py-1.5 text-xs font-black text-indigo-300 outline-none focus:ring-2 focus:ring-indigo-500/50"
                        >
                            <option value="ALL">TODOS LOS MESES</option>
                            {mesesDisponibles.map(m => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>
                    </div>
                    <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                        {filteredGfs.length === 0 ? (
                            <div className="py-12 text-center opacity-30">
                                <div className="text-5xl mb-3">📅</div>
                                <p className="text-xs italic font-black uppercase tracking-widest">Sin registros para {filterMonth === 'ALL' ? 'ningún mes' : filterMonth}</p>
                            </div>
                        ) : (
                            filteredGfs.map(gf => (
                                <div
                                    key={gf.id}
                                    className="flex items-center justify-between p-5 rounded-2xl border transition-all cursor-pointer group/card bg-white/5 border-white/10 hover:bg-white/10"
                                    onClick={() => {
                                        const metadata = {
                                            cedula: configuracion?.cedula,
                                            rp: configuracion?.numero_rp,
                                            mes: gf.mes_nombre
                                        };
                                        abrirPreview(`${BACKEND_URL}${gf.pdf_url}`, metadata);
                                    }}
                                >
                                    <div className="min-w-0 pr-4">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="font-black text-xs uppercase tracking-tighter text-indigo-400">GF {gf.mes_nombre}</div>
                                            <span className="text-[9px] font-bold text-slate-500 bg-white/5 px-2 py-0.5 rounded-full">{formatFecha(gf.fecha_creacion)}</span>
                                        </div>
                                        <div className="text-[10px] text-slate-400 truncate font-mono uppercase tracking-tight">
                                            GF_{configuracion?.cedula || 'ID'}_{configuracion?.numero_rp || 'RP'}_{gf.mes_nombre}.pdf
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                const metadata = {
                                                    cedula: configuracion?.cedula,
                                                    rp: configuracion?.numero_rp,
                                                    mes: gf.mes_nombre
                                                };
                                                const fileName = `GF_${metadata.cedula || 'ID'}_${metadata.rp || 'RP'}_${metadata.mes || 'MES'}.pdf`.replace(/\s+/g, '_');

                                                try {
                                                    const response = await apiClient.get(gf.pdf_url.replace('/api', ''), { responseType: 'blob' });
                                                    const cleanBlob = new Blob([response.data], { type: 'application/pdf' });
                                                    const url = window.URL.createObjectURL(cleanBlob);
                                                    const link = document.createElement('a');
                                                    link.href = url;
                                                    link.setAttribute('download', fileName);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    link.remove();
                                                    window.URL.revokeObjectURL(url);
                                                } catch (err) {
                                                    showNotification(getErrorMessage(err, 'Error al descargar el archivo'), 'error');
                                                }
                                            }}
                                            className="p-2.5 bg-indigo-500/10 text-indigo-400 rounded-xl hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                                            title="Descargar PDF"
                                        >
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleDeleteGf(gf.id); }}
                                            className="p-2.5 bg-red-500/10 text-red-400 rounded-xl hover:bg-red-500 hover:text-white transition-all opacity-0 group-hover/card:opacity-100"
                                            title="Eliminar"
                                        >
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

            </div>

            {/* Modal de Vista Previa del PDF */}
            {previewConfig && (
                <PdfPreviewModal
                    isOpen={!!previewConfig}
                    onClose={() => setPreviewConfig(null)}
                    {...previewConfig}
                />
            )}

            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar { width: 10px; height: 10px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; border: 2px solid #f1f5f9; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            `}} />
        </div>
    );
};

export default GfMerger;
